import { useMutation } from '@tanstack/react-query';
import { Col, DatePicker, Form, Row } from 'antd';
import locale from 'antd/es/date-picker/locale/en_US';
import { DefaultOptionType } from 'antd/es/select';
import dayjs from 'dayjs';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { IGetOrderByCustomer } from 'apis/GraphQL/Variables/salesInvoice';
import { PlantApi } from 'apis/PlantApis';
import { SalesInfoApis } from 'apis/SalesInfoApis';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { FormFilter, InputForm, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import { IGetStatusOrder } from 'interfaces/SalesInfo';
import { IFilterTeamSaleCb } from 'interfaces/TeamSale/TeamSale';
import Content from 'layouts/Content';
import { toastError } from 'utils/constants/functions';

const colSpan = 6;
const { RangePicker } = DatePicker;

interface IFilterSalesInvoicePage {
  isOpen: boolean;
  defaultFilter: any;
  onFinish(params: any): void;
  onCancel: () => void;
}

export type IFilterSaleInvoiceForm = Omit<
  IGetOrderByCustomer,
  'page' | 'pageSize'
>;

export const FilterSalesInvoicePage = memo(
  forwardRef(
    (
      { isOpen, defaultFilter, onCancel, onFinish }: IFilterSalesInvoicePage,
      ref,
    ) => {
      const { user } = useUser();
      const location = useLocation();
      const [form] = Form.useForm<IFilterSaleInvoiceForm>();

      const [plantOptionData, setPlantOptionData] = useState(
          [] as { id: string; name: string }[],
        ),
        [orderTypeOptions, setOrderTypeOptions] = useState(
          [] as DefaultOptionType[],
        ),
        [orderStatusOptions, setOrderStatusOptions] = useState(
          [] as DefaultOptionType[],
        ),
        [prodcessStatus, setOrderProcessStatus] = useState(
          [] as DefaultOptionType[],
        ),
        [teamData, setTeamData] = useState(initLazySelectData),
        [plantId, setPlantId] = useState<string | undefined>(undefined);

      const {
          mutate: getAllPlantComboBoxMutate,
          isPending: isGetAllPlantComboBox,
        } = useMutation({
          mutationFn: () => PlantApi.getAllPlantComboBox(),
          onSuccess: (res: any) => {
            if (res.data?.getPlant) {
              setPlantOptionData(
                (res.data?.getPlant?.items ?? []).map(
                  (item: any) =>
                    ({
                      label: item?.id,
                      value: item?.id,
                    }) as DefaultOptionType,
                ),
              );
              return;
            }

            if (res?.errors) toastError(`Lỗi. ${res?.errors[0]?.message}`);
          },
          onError: (error) => {
            toastError(`Lỗi. ${error?.message ?? error}`);
          },
        }),
        {
          mutate: getOrderStatusComboBoxMutate,
          isPending: isGetOrderStatusComboBoxMutatePending,
        } = useMutation({
          mutationFn: (
            params: IGetStatusOrder & { type: 'type' | 'status' | 'process' },
          ) => SalesInfoApis.getOrderStatusComboBox(params),
          onSuccess: (res: any, variables) => {
            if (res?.data) {
              const options = (res?.data?.getCode?.items ?? []).map(
                (item: any) =>
                  ({
                    label: item?.name,
                    value: item?.id,
                  }) as DefaultOptionType,
              );

              if (variables.type === 'type') setOrderTypeOptions(options);
              if (variables.type === 'status') setOrderStatusOptions(options);
              if (variables.type === 'process') setOrderProcessStatus(options);

              if ((res?.errors ?? []).length > 0)
                toastError(
                  `Lỗi khi lấy danh sách ${variables.type === 'type' ? 'loại' : 'trạng thái'}. ${res?.errors[0]?.message}`,
                );
            }
          },
          onError: (error, variables) => {
            toastError(
              `Lỗi khi lấy danh sách ${variables.type === 'type' ? 'loại' : 'trạng thái'}. ${error?.message ?? error}`,
            );
          },
        }),
        { isPending: isGetTeamSaleCbb, mutate: getTeamSaleCbb } = useMutation({
          mutationFn: (params: IFilterTeamSaleCb) =>
            SaleTeamApi.getAllTeamSaleCb(params),
          onSuccess: (res) => {
            if (res?.data?.getTeamSales) {
              const data = res.data.getTeamSales;

              setTeamData({
                options: (data?.items ?? []).map(
                  (item: any) =>
                    ({
                      label: `${item?.subId} - ${item?.name}`,
                      value: `${item?.id}.${item?.subId}.${item?.plantId}`,
                    }) as DefaultOptionType,
                ),
                totalItems: res?.data?.getTeamSales.pageInfo.totalItems ?? 0,
              });
            }
          },
          onError: (error) => {
            console.log(error);
          },
        });

      useEffect(() => {
        if (!isOpen) return;

        getAllPlantComboBoxMutate();
        ['type', 'status', 'process'].forEach((type: any) =>
          getOrderStatusComboBoxMutate(
            type === 'type'
              ? {
                  groupId: 'OR02',
                  id: 'OR02_01',
                  type,
                }
              : type === 'status'
                ? { groupId: '109', type }
                : { groupId: 'OR01', type },
          ),
        );
      }, [isOpen]);

      useEffect(() => {
        if (!isOpen) return;

        getTeamSaleCbb({ plantId: plantId, pageSize: 10 });
      }, [plantId, isOpen]);

      const handleSetFormValues = useCallback(
        (values: IFilterSaleInvoiceForm) => {
          form.setFieldsValue(values);
        },
        [],
      );

      const handleOnCancel = useCallback(() => {
        form.resetFields();
        form.setFieldsValue({
          ...defaultFilter,
          'createFrom - createTo': [
            dayjs().subtract(1, 'month').startOf('day'),
            null,
          ],
        });

        onCancel();
      }, [onCancel, defaultFilter]);

      const handleOnFilter = useCallback(() => {
        const formValues = form.getFieldsValue();

        onFinish({
          ...formValues,
          orderId: formValues['orderId']?.trim(),
          // teamSalesId: formValues?.teamSalesId?.split('.')?.[0],
        });
      }, [form, location]);

      const handleTeamSelectScrollChange = useCallback(
        (params: ILazySelectParams) => {
          if (!isOpen) return;

          const { pageSize, value } = params;

          getTeamSaleCbb({ plantId: plantId, pageSize: pageSize, value });
        },
        [isOpen, plantId],
      );

      const onPlantSelect = useCallback((value: string) => {
        setPlantId(value);
      }, []);

      const onPlantClearSelect = useCallback(() => {
        setPlantId(undefined);
      }, []);

      useImperativeHandle(ref, () => ({
        handleOnCancel,
        handleSetFormValues,
      }));

      return (
        <Content isOpen={isOpen}>
          <FormFilter
            form={form}
            onFinish={handleOnFilter}
            onCancel={handleOnCancel}
          >
            <Row gutter={{ xs: 8, sm: 14 }}>
              <Col span={colSpan} className="h-[80px]">
                <Form.Item label="Mã bảng kê" name="orderId">
                  <InputForm name="orderId" placeholder="Tìm mã bảng kê" />
                </Form.Item>
              </Col>
              <Col span={colSpan} className="h-[80px]">
                <Form.Item label="Mã đơn SO" name="SoId">
                  <InputForm name="SoId" placeholder="Tìm mã đơn SO" />
                </Form.Item>
              </Col>
              <Col span={colSpan} className="h-[80px]">
                <Form.Item label="Tìm hoá đơn điện tử" name="BillingId">
                  <InputForm
                    name="BillingId"
                    placeholder="Tìm hóa đơn điện tử"
                  />
                </Form.Item>
              </Col>
              <Col span={colSpan} className="h-[80px]">
                <Form.Item
                  label="Ngày tạo từ - đến"
                  name="createFrom - createTo"
                >
                  <RangePicker
                    size="large"
                    locale={locale}
                    format="DD/MM/YYYY"
                    style={{ width: '100%' }}
                    placeholder={['Từ ngày', 'Đến ngày']}
                  />
                </Form.Item>
              </Col>
              <Col span={12} className="h-[80px]">
                <Form.Item label="Mã KH" name="customerId">
                  <InputForm
                    name="customerId"
                    placeholder="Tìm MST/Mã khách hàng"
                  />
                </Form.Item>
              </Col>
              <Col span={colSpan} className="h-[80px]">
                <Form.Item label="Mã KH cha" name="maincustomerId">
                  <InputForm
                    name="maincustomerId"
                    placeholder="Tìm MST/Mã khách hàng cha"
                  />
                </Form.Item>
              </Col>
              <Col span={colSpan} className="h-[80px]">
                <Form.Item label="Đơn gộp/tách" name="orderType">
                  <SelectForm
                    options={orderTypeOptions}
                    placeholder="Chọn đơn gộp/tách"
                    loading={isGetOrderStatusComboBoxMutatePending}
                  />
                </Form.Item>
              </Col>
              <Col span={colSpan} className="h-[80px]">
                <Form.Item label="Chi nhánh" name="plantId">
                  <SelectForm
                    options={plantOptionData}
                    placeholder="Chọn chi nhánh"
                    loading={isGetAllPlantComboBox}
                    disabled={user?.isAccountant || user?.isTreasurer}
                    onSelect={onPlantSelect}
                    onClear={onPlantClearSelect}
                  />
                </Form.Item>
              </Col>
              <Col span={colSpan} className="h-[80px]">
                <Form.Item label="Đội bán hàng" name="teamSalesId">
                  <LazySelectForm
                    data={teamData}
                    loading={isGetTeamSaleCbb}
                    placeholder="Chọn đội bán hàng"
                    onSelectScrollChange={handleTeamSelectScrollChange}
                  />
                </Form.Item>
              </Col>
              <Col span={colSpan} className="h-[80px]">
                <Form.Item label="Loại đơn" name="orderTypeId">
                  <SelectForm
                    options={orderStatusOptions}
                    placeholder="Chọn loại đơn"
                  />
                </Form.Item>
              </Col>
              <Col span={colSpan} className="h-[80px]">
                <Form.Item label="Trạng thái" name="orderStatus">
                  <SelectForm
                    options={prodcessStatus}
                    placeholder="Chọn trạng thái"
                  />
                </Form.Item>
              </Col>
            </Row>
          </FormFilter>
        </Content>
      );
    },
  ),
);
